import { Tab, Tabs } from '@blueprintjs/core';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';

import PersonCardLoader from './personCardLoader';

const PersonGroupTabs = ({
  isLoading,
  onChange,
  panel,
  people,
  personGroups,
  selectedPersonGroup,
  treeId,
  includeCitations = true,
}) => {
  // If there are no people, remove all groups, that require them.
  const displayedPersonGroups = useMemo(
    () =>
      people.length === 0 && !isLoading
        ? personGroups.filter(group => !group.peopleRequired)
        : personGroups,
    [isLoading, people, personGroups],
  );

  const handleChange = useCallback(
    selectedId => {
      const selectedGroup = displayedPersonGroups.find(group => group.id === selectedId);
      if (selectedGroup == null) {
        throw new Error(`Unknown person group: ${selectedId}`);
      }
      onChange(selectedGroup);
    },
    [displayedPersonGroups, onChange],
  );

  // If the selected group isn't displayed, select the first one available to display.
  useEffect(() => {
    const selectedId = selectedPersonGroup.id;
    const hasSelected = displayedPersonGroups.some(group => group.id === selectedId);
    if (!hasSelected) {
      onChange(displayedPersonGroups[0]);
    }
  }, [displayedPersonGroups, onChange, selectedPersonGroup]);

  // Display loader if people are loading and not yet ready.
  // Only after people are loaded, it can be determined if the tabs should be displayed.
  if (isLoading) {
    return <PersonCardLoader />;
  }

  // If there is no group available, display nothing.
  if (displayedPersonGroups.length === 0) {
    return null;
  }

  // If there is only one group available, don't display the tabs.
  if (displayedPersonGroups.length === 1) {
    const [group] = displayedPersonGroups;
    return <group.TabPanel treeId={treeId} panel={panel} people={people} personGroup={group} includeCitations={includeCitations} />;
  }

  // Display multiple groups in tabs.
  return (
    <Tabs large onChange={handleChange} selectedTabId={selectedPersonGroup.id}>
      {displayedPersonGroups.map(group => (
        <Tab
          id={group.id}
          key={group.id}
          panel={
            <group.TabPanel treeId={treeId} panel={panel} people={people} personGroup={group} includeCitations={includeCitations} />
          }
          title={group.title}
        />
      ))}
    </Tabs>
  );
};

PersonGroupTabs.defaultProps = {
  isLoading: false,
  onChange: () => {},
  people: [],
};

PersonGroupTabs.propTypes = {
  isLoading: PropTypes.bool,
  onChange: PropTypes.func,
  panel: PropTypes.node.isRequired,
  people: PropTypes.arrayOf(PropTypes.object),
  personGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedPersonGroup: PropTypes.object.isRequired,
  treeId: PropTypes.string.isRequired,
  includeCitations: PropTypes.bool,
};

export default PersonGroupTabs;
